import { staleTimes, useQuery } from '@platform/api-client';
import { formatMoney, formatNumber, formatPercent, formatRate } from '@platform/design-system';
import type { Operation, ResponseBody } from '@platform/openapi';
import type { ReportPeriod } from '../report-period.ts';
import { queryKeys, usePageContext } from '../usePageContext.ts';

export const STATISTICS_TABS = ['delivery', 'confirmation', 'products', 'orders', 'marketing'] as const;
export type StatisticsTab = (typeof STATISTICS_TABS)[number];

export const isStatisticsTab = (value: string): value is StatisticsTab =>
  STATISTICS_TABS.some((tab) => tab === value);

export type StatisticsPath = `/statistics/${StatisticsTab}`;

export const statisticsPathOf = (tab: StatisticsTab): StatisticsPath => `/statistics/${tab}`;

export type DeliveryReport = ResponseBody<Operation<'/admin/statistics/delivery', 'get'>>;
export type ConfirmationReport = ResponseBody<Operation<'/admin/statistics/confirmation', 'get'>>;
export type OrdersReport = ResponseBody<Operation<'/admin/statistics/orders', 'get'>>;
export type MarketingReport = ResponseBody<Operation<'/admin/statistics/marketing', 'get'>>;
export type ProductsReport = ResponseBody<Operation<'/admin/statistics/products', 'get'>>;
export type StageTotal = OrdersReport['stages']['all'];

export const STATISTICS_ROLE = 'finance:read';

export const ORDER_STAGE_TILES = [
  'all',
  'pending',
  'confirmed',
  'rejected',
  'depot',
  'transit',
  'delivered',
  'returned',
] as const satisfies readonly (keyof OrdersReport['stages'])[];

const MINUTES_PER_HOUR = 60;
const SECONDS_PER_MINUTE = 60;
const DAY_DECIMALS = 1;

export const rateOf = (part: number, whole: number): number | null => (whole === 0 ? null : part / whole);

export const durationPartsOf = (seconds: number): { readonly hours: number; readonly minutes: number } => {
  const minutes = Math.round(seconds / SECONDS_PER_MINUTE);
  return { hours: Math.floor(minutes / MINUTES_PER_HOUR), minutes: minutes % MINUTES_PER_HOUR };
};

export const useStatisticsReport = <Report>(
  tab: StatisticsTab,
  period: ReportPeriod,
  filters: Readonly<Record<string, string | null>>,
  fetch: () => Promise<Report>,
) => {
  const { tenantId } = usePageContext();
  return useQuery({
    queryKey: queryKeys.statistics(tenantId, { tab, ...period, ...filters }),
    queryFn: fetch,
    staleTime: staleTimes.dashboard,
  });
};

export const useStatisticsFormat = (currency: string) => {
  const { t, locale } = usePageContext();
  return {
    count: (value: number) => formatNumber(value, locale),
    money: (amount: string | null) => (amount === null ? '—' : formatMoney({ amount, currency }, locale)),
    rate: (value: number | null) => (value === null ? '—' : formatRate(value, locale)),
    share: (value: number | null) => (value === null ? '—' : formatPercent(value, locale)),
    days: (value: number | null) =>
      value === null ? '—' : t('statistics.days', { value: formatNumber(value, locale, DAY_DECIMALS) }),
    duration: (seconds: number | null) => {
      if (seconds === null) return '—';
      const { hours, minutes } = durationPartsOf(seconds);
      return hours === 0
        ? t('statistics.minutes', { minutes })
        : t('statistics.hoursMinutes', { hours, minutes });
    },
  };
};

export type StatisticsFormat = ReturnType<typeof useStatisticsFormat>;
